import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { get, has } from "lodash";
import { v4 as generateUUID } from "uuid";
import mediator from "@tvg/mediator";
import tvgConf from "@tvg/conf";
// @ts-ignore
import Context from "@fdr-mar/promos-context";
import { CardSlot } from "@fdr-mar/promos-types/Component";
import { getSession } from "@urp/unified-modules/src/utils/account";
import { isXSell } from "@tvg/sh-utils/mobileUtils";
import { isFDR } from "@tvg/utils/generalUtils";

import { ContentProps, Blok, User, Promo } from "../../types";
import HandicappingAndSeoRow from "../../components/HandicappingAndSeoRow";
import Components from "../../_dictionary";
import HomePageContainer from "./styled-components";
import { onbackPromosModal } from "../../redux/actions";

const { UserContext, OptinContext } = Context;

type Platform = "ios" | "web" | "android" | "androidGPS";

interface Props {
  content: ContentProps;
  user: User;
  platform: Platform;
  mobile: boolean;
  appVersion: string;
}

export const HomePage: React.FC<Props> = ({
  content,
  user,
  platform,
  mobile,
  appVersion
}) => {
  const userContext = useContext(UserContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isPromoBannerOptedIn, setIsPromoBannerOptedIn] = useState(false);

  useEffect(() => {
    // GTM dataLayer PAGEVIEW push
    let currentPlatform = platform;
    // @ts-ignore
    const { currentSlug } = userContext;
    const loginStatus = get(user, "isLogged", false)
      ? "Logged In"
      : "Logged Out";
    const registrationStatus = get(user, "returningUser", false)
      ? "Registered"
      : "Unregistered";
    let siteVersion;

    if (tvgConf().product === "androidwrapper") {
      currentPlatform = "androidGPS";
    } else if (tvgConf().product.includes("fdrandroid")) {
      currentPlatform = "android";
    }

    if (
      mobile &&
      currentPlatform !== "ios" &&
      currentPlatform !== "android" &&
      currentPlatform !== "androidGPS"
    ) {
      siteVersion = "v2-revamp";
    } else if (mobile && currentPlatform === "ios") {
      siteVersion = "iOS";
    } else if (mobile && currentPlatform === "android") {
      siteVersion = "fdrandroid";
    } else if (mobile && currentPlatform === "androidGPS") {
      siteVersion = "fdrandroidgps";
    } else {
      siteVersion = "TVG4";
    }

    mediator.base.dispatch({
      type: "PROMOS_PAGEVIEW",
      payload: {
        siteVersion,
        appVersion,
        accountId: get(user, "accountNumber", undefined),
        residenceState: get(user, "homeState", undefined),
        loginStatus,
        registrationStatus,
        page: currentSlug,
        promoSource: "Home Page"
      }
    });
  }, [user, mobile, appVersion, platform, userContext]);

  const promotionDate = (date: string): Date => {
    const parsedDate = date.split(/[T ]/g);
    const dateParsed = parsedDate[0].split("-");
    const time = parsedDate[1].split(":");
    const localDate = new Date(
      Number(dateParsed[0]),
      Number(dateParsed[1]) - 1,
      Number(dateParsed[2]),
      Number(time[0]),
      Number(time[1])
    );

    const pacificDate = new Date(
      localDate.toLocaleString("en-US", { timeZone: "UTC" })
    );
    const localTimeOffset = localDate.getTime() - pacificDate.getTime();

    return new Date(localDate.getTime() + localTimeOffset);
  };

  const filterBannerLogic = (blok: Blok): Blok | null => {
    const segment = get(blok, "config.segment", "");

    if (segment === "ret" && (user.isLogged || user.returningUser)) {
      return blok;
    }

    if (segment === "acq" && !user.returningUser && !user.isLogged) {
      return blok;
    }

    if (segment === "all") {
      return blok;
    }

    return null;
  };

  const filterBanners = (blok: Blok): Blok | null => {
    if (get(blok, "startDate") && get(blok, "endDate")) {
      const start = promotionDate(get(blok, "startDate"));
      const end = promotionDate(get(blok, "endDate"));
      return start <= new Date(Date.now()) && new Date(Date.now()) <= end
        ? filterBannerLogic(blok)
        : null;
    }

    if (get(blok, "startDate")) {
      const start = promotionDate(get(blok, "startDate"));
      return start <= new Date(Date.now()) ? filterBannerLogic(blok) : null;
    }

    if (get(blok, "endDate")) {
      const end = promotionDate(get(blok, "endDate"));
      return new Date(Date.now()) <= end ? filterBannerLogic(blok) : null;
    }
    return filterBannerLogic(blok);
  };

  const filterSlots = (slots: CardSlot[]): CardSlot[] => {
    const filteredSlots: CardSlot[] = [];
    slots
      .filter(({ startDate, endDate }) => {
        if (startDate && endDate) {
          const start = promotionDate(startDate);
          const end = promotionDate(endDate);
          return start <= new Date(Date.now()) && new Date(Date.now()) <= end;
        }

        if (startDate) {
          const start = promotionDate(startDate);
          return start <= new Date(Date.now());
        }

        if (endDate) {
          const end = promotionDate(endDate);
          return new Date(Date.now()) <= end;
        }
        return true;
      })
      .forEach((slot) => {
        if (slot.segment === "all") {
          filteredSlots.push(slot);
        }

        if (slot.segment === "acq" && !user.returningUser && !user.isLogged) {
          filteredSlots.push(slot);
        }

        if (slot.segment === "ret" && (user.returningUser || user.isLogged)) {
          const promoId = get(slot, "promoId");
          const eligiblePromo = has(
            user,
            `optedInPromos.${promoId && promoId.toString()}`
          );

          if (eligiblePromo) {
            filteredSlots.push(slot);
          }
        }
      });

    return filteredSlots;
  };

  /* eslint-disable no-underscore-dangle */

  const toggleComponents = (): Blok[] => {
    const result: Blok[] = [];
    let bannerToDisplay: Blok | null = null;
    let seoContent: Blok | null = null;
    let isOptedIn = false;

    content.body.forEach((blok) => {
      switch (blok.component) {
        case "banner": {
          const bannerComponent = filterBanners(blok);

          if (bannerComponent) {
            bannerToDisplay = bannerComponent;
            isOptedIn = get(
              user.optedInPromos,
              bannerComponent.config.promoId,
              false
            );

            if (isOptedIn && !isPromoBannerOptedIn) {
              setIsPromoBannerOptedIn(isOptedIn);
            }
          }

          break;
        }
        case "all_promos":
        case "top_promos": {
          const isTopPromo = blok.component === "top_promos";
          const slots = get(blok, "slots");

          if (slots && slots.length > 0) {
            const filteredSlots = filterSlots(slots) as Promo[];

            if (filteredSlots.length > 0) {
              const promoData: Blok = {
                header: get(blok, "header"),
                component: blok.component,
                slots: filteredSlots,
                _uid: blok._uid,
                config: blok.config
              };

              if (isTopPromo) {
                // Ensure top_promos are added to the beginning of the result array
                result.unshift(promoData);
              } else {
                result.push(promoData);
              }
            }
          }

          break;
        }
        case "legal_content": {
          const legalsBannerLink = get(blok, "promoCode");

          if (bannerToDisplay !== null && legalsBannerLink) {
            const bannerPromoCode = get(bannerToDisplay, "config.promoCode");
            const bannerPromoId = get(bannerToDisplay, "config.promoId");

            if (
              bannerPromoCode === legalsBannerLink ||
              bannerPromoId === legalsBannerLink
            ) {
              result.push(blok);
            }
          }

          break;
        }
        case "seo_content": {
          seoContent = blok;
          break;
        }
        case "footer": {
          if (mobile) {
            result.push(blok);
          }

          break;
        }
        default:
          result.push(blok);
      }
    });

    if (seoContent) {
      result.push({
        component: "handicapping_and_seo",
        // @ts-ignore
        seoContent
      });
    } else if (!isXSell()) {
      // @ts-ignore
      result.push({
        component: "handicapping",
        _uid: generateUUID()
      });
    }

    // Ensure that the correct banner component is set as the first component to render
    // on the array after parsing through all potential banner segment types
    if (bannerToDisplay !== null) {
      result.unshift(bannerToDisplay);
    }

    return result;
  };

  const handleOptinClick = (url: string, slug: string) => {
    const navigateToPromo = () => {
      mediator.base.dispatch({
        type: "PROMOS_MODAL:NEW_SLUG",
        payload: { newSlug: slug }
      });
      navigate(url);
      dispatch(onbackPromosModal(true));
    };

    const isSignUpFlow =
      url && (url.includes("registration") || url.includes("sign-up"));

    if (user.isLogged) {
      navigateToPromo();
    } else if (!isFDR()) {
      if (isSignUpFlow) {
        navigate("/registration");
      } else {
        mediator.base.dispatch({
          type: "OPEN_LOGIN",
          payload: {
            callback: (_: null, success: Object) => {
              if (get(success, "status", "fail") === "success") {
                navigateToPromo();
              }
            }
          }
        });
      }
    } else {
      const sessionType = isSignUpFlow ? "NewUser" : "Normal";
      getSession(sessionType, () => {
        navigate(url);
        mediator.base.dispatch({
          type: "PROMOS_MODAL:NEW_SLUG",
          payload: { newSlug: slug }
        });
        dispatch(onbackPromosModal(true));
      });
    }
  };

  const { accountNumber, isLogged, returningUser, optedInPromos } = user;
  const toggledContent = toggleComponents();

  return (
    <UserContext.Provider
      value={{ accountNumber, isLogged, returningUser, optedInPromos }}
    >
      <OptinContext.Provider
        value={{ isOpted: isPromoBannerOptedIn, handleOptinClick }}
      >
        <HomePageContainer data-qa-label="homePageContainer">
          {toggledContent.map((blok) => {
            if (blok.component === "handicapping_and_seo") {
              return (
                <HandicappingAndSeoRow
                  key={blok._uid}
                  // @ts-ignore
                  seoContent={blok.seoContent}
                />
              );
            }
            return Components(blok);
          })}
        </HomePageContainer>
      </OptinContext.Provider>
    </UserContext.Provider>
  );
};

export default HomePage;
